import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { useAuth } from "@/context/AuthContext";

// Route guard driven by the server-resolved feature-access model (see
// useFeatureAccess). Same redirect rules as FeatureGate: spinner while
// loading, /login for anonymous users after a grace period, / for logged-in
// users without access.
export function FeatureAccessGate({ feature, children }) {
  const { canAccess, loading } = useFeatureAccess(feature);
  const { user, loading: authLoading } = useAuth();
  const [graceExpired, setGraceExpired] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!loading && !authLoading && !user) {
      timerRef.current = setTimeout(() => setGraceExpired(true), 800);
      return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }
    setGraceExpired(false);
  }, [loading, authLoading, user]);

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]" data-testid="feature-access-gate-loading">
        <Loader2 className="w-7 h-7 animate-spin text-primary" />
      </div>
    );
  }

  if (canAccess) return children;
  if (!user && graceExpired) return <Navigate to="/login" replace />;
  if (user) return <Navigate to="/" replace />;
  return (
    <div className="flex items-center justify-center min-h-[300px]" data-testid="feature-access-gate-loading">
      <Loader2 className="w-7 h-7 animate-spin text-primary" />
    </div>
  );
}

export default FeatureAccessGate;
